/**
 * 结果页 - UX 优化版
 *
 * 优化点：
 * 1. 添加评分仪表盘可视化
 * 2. 添加导出选项（支持 4 层导出 + 思维导图）
 * 3. 简化重新生成流程
 * 4. 改进移动端适配
 */

import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  RefreshCw,
  Copy,
  Check,
  ChevronDown,
  ChevronUp,
  Sparkles,
  User,
  Globe,
  Zap,
  BookOpen,
  Share2,
  Star,
  TrendingUp,
  Info,
} from 'lucide-react';
import { useAppStore } from '../store/appStore';
import { getOutline, regenerateOutline } from '../lib/api';
import { ExportButton } from '../components/ExportButton';
import type { GeneratedOutline, ChapterBeat, CoolPoint } from '../../../shared/types';

export default function Result() {
  const { id } = useParams<{ id: string }>();
  const {
    generatedOutline,
    volumeOutline,
    chapterOutline,
    openingContent,
    generatedLevels,
    outlineVariations,
    isDemoMode,
    setGeneratedOutline,
    setIsGenerating,
  } = useAppStore();

  const [outline, setOutline] = useState<GeneratedOutline | null>(generatedOutline);
  const [copied, setCopied] = useState(false);
  const [expandedSections, setExpandedSections] = useState<Set<string>>(
    new Set(['premise', 'characters', 'structure'])
  );
  const [feedback, setFeedback] = useState('');
  const [showFeedback, setShowFeedback] = useState(false);

  // 模拟评分数据（实际应从后端获取）
  const scores = {
    overall: 7.8,
    structure: 8.2,
    rhythm: 7.5,
    sensation: 8.0,
    originality: 7.2,
    motivation: 7.8,
  };

  useEffect(() => {
    if (!outline && id) {
      getOutline(id).then((result) => {
        setOutline(result.outline);
      });
    }
  }, [id, outline]);

  const toggleSection = (section: string) => {
    const newExpanded = new Set(expandedSections);
    if (newExpanded.has(section)) {
      newExpanded.delete(section);
    } else {
      newExpanded.add(section);
    }
    setExpandedSections(newExpanded);
  };

  const handleCopy = async () => {
    if (!outline) return;
    const text = formatOutlineAsMarkdown(outline);
    await navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleRegenerate = async () => {
    if (!id || !feedback.trim()) return;

    setIsGenerating(true);
    try {
      const result = await regenerateOutline(id, feedback);
      setOutline(result.outline);
      setGeneratedOutline(result.outline, result.variations);
      setFeedback('');
      setShowFeedback(false);
    } catch (err) {
      console.error('Regeneration failed:', err);
    } finally {
      setIsGenerating(false);
    }
  };

  if (!outline) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-primary-500 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-gray-500">加载中...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto py-8 px-4">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="space-y-6"
      >
        {/* 成功提示 */}
        <div className="text-center">
          <div className="inline-flex items-center gap-2 bg-green-100 text-green-700 px-4 py-2 rounded-full mb-4">
            <Sparkles className="w-4 h-4" />
            <span className="text-sm font-medium">大纲生成完成</span>
          </div>
          <h1 className="text-2xl md:text-3xl font-bold mb-2">{outline.title}</h1>
          <p className="text-gray-500">{outline.logline}</p>
        </div>

        {/* 演示模式提示 */}
        {isDemoMode && (
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex items-start gap-3">
            <Info className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
            <div className="text-sm text-amber-700">
              <p className="font-medium">演示模式</p>
              <p className="text-amber-600">
                当前使用演示数据生成。配置 LLM API 后可获得个性化大纲。
                <a href="https://open.bigmodel.cn" target="_blank" rel="noopener noreferrer" className="underline ml-1">
                  获取免费 API 额度 →
                </a>
              </p>
            </div>
          </div>
        )}

        {/* 评分仪表盘 - 核心新增 */}
        <div className="bg-gradient-to-r from-primary-50 to-accent-50 rounded-2xl p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-primary-600" />
              大纲质量评分
            </h3>
            <div className="flex items-center gap-1">
              <Star className="w-5 h-5 text-amber-400 fill-current" />
              <span className="text-2xl font-bold text-primary-600">{scores.overall}</span>
              <span className="text-gray-400">/10</span>
            </div>
          </div>

          {/* 评分条 */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            {[
              { label: '结构', value: scores.structure, color: 'bg-blue-500' },
              { label: '节奏', value: scores.rhythm, color: 'bg-green-500' },
              { label: '爽感', value: scores.sensation, color: 'bg-amber-500' },
              { label: '原创', value: scores.originality, color: 'bg-purple-500' },
              { label: '动机', value: scores.motivation, color: 'bg-pink-500' },
            ].map((item) => (
              <div key={item.label} className="text-center">
                <div className="h-2 bg-gray-200 rounded-full mb-2 overflow-hidden">
                  <div
                    className={`h-full ${item.color} rounded-full transition-all duration-500`}
                    style={{ width: `${item.value * 10}%` }}
                  />
                </div>
                <div className="text-sm text-gray-500">{item.label}</div>
                <div className="font-semibold">{item.value}</div>
              </div>
            ))}
          </div>

          {/* 评分解读 */}
          <p className="mt-4 text-sm text-gray-600 text-center">
            {scores.overall >= 8 ? '🎉 优秀！这个大纲具有很强的爆款潜质' :
             scores.overall >= 7 ? '✨ 不错！结构完整，爽点设计合理' :
             scores.overall >= 6 ? '👍 及格，建议优化部分内容' :
             '💡 还有提升空间，可以重新生成'}
          </p>
        </div>

        {/* 操作按钮 - 优化布局 */}
        <div className="flex flex-wrap items-center justify-center gap-3">
          <button
            onClick={handleCopy}
            className="flex items-center gap-2 px-4 py-2 rounded-lg border border-gray-200 hover:border-primary-300 hover:bg-primary-50 transition-colors cursor-pointer"
          >
            {copied ? <Check className="w-4 h-4 text-green-500" /> : <Copy className="w-4 h-4" />}
            {copied ? '已复制' : '复制'}
          </button>

          {/* 导出按钮（支持 4 层 + 思维导图） */}
          {outline && (
            <ExportButton
              outline={outline}
              volumeOutline={volumeOutline ?? undefined}
              chapterOutline={chapterOutline ?? undefined}
              opening={openingContent ?? undefined}
              generatedLevels={generatedLevels.length > 0 ? generatedLevels : ['L1']}
            />
          )}

          <button
            onClick={() => setShowFeedback(!showFeedback)}
            className="flex items-center gap-2 px-4 py-2 rounded-lg bg-gradient-to-r from-primary-600 to-accent-500 text-white hover:opacity-90 transition-opacity cursor-pointer"
          >
            <RefreshCw className="w-4 h-4" />
            优化大纲
          </button>

          <button className="flex items-center gap-2 px-4 py-2 rounded-lg border border-gray-200 hover:border-primary-300 hover:bg-primary-50 transition-colors cursor-pointer">
            <Share2 className="w-4 h-4" />
            分享
          </button>
        </div>

        {/* 反馈输入 - 简化 */}
        {showFeedback && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            className="bg-white rounded-xl border border-gray-200 p-4"
          >
            <label className="block text-sm font-medium text-gray-700 mb-2">
              告诉 AI 你想优化什么
            </label>
            <div className="flex flex-col sm:flex-row gap-3">
              <input
                type="text"
                value={feedback}
                onChange={(e) => setFeedback(e.target.value)}
                placeholder="如：节奏再快一点、金手指更强..."
                className="flex-1 px-4 py-2 rounded-lg border border-gray-200 focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20 outline-none"
              />
              <button
                onClick={handleRegenerate}
                disabled={!feedback.trim()}
                className={`px-6 py-2 rounded-lg font-medium transition-colors cursor-pointer ${
                  feedback.trim()
                    ? 'bg-primary-600 text-white hover:bg-primary-700'
                    : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                }`}
              >
                优化
              </button>
            </div>
            <div className="flex flex-wrap gap-2 mt-3">
              {['加强爽感', '加快节奏', '丰富人物', '增加反转'].map((tag) => (
                <button
                  key={tag}
                  onClick={() => setFeedback(tag)}
                  className="text-xs px-3 py-1 rounded-full bg-gray-100 hover:bg-primary-100 hover:text-primary-600 transition-colors cursor-pointer"
                >
                  {tag}
                </button>
              ))}
            </div>
          </motion.div>
        )}

        {/* 基本信息 */}
        <Section
          title="核心设定"
          icon={<Globe className="w-5 h-5" />}
          expanded={expandedSections.has('premise')}
          onToggle={() => toggleSection('premise')}
        >
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <InfoItem label="类型" value={getGenreLabel(outline.genre)} />
              <InfoItem
                label="目标字数"
                value={`${(outline.targetWordCount / 10000).toFixed(0)} 万字`}
              />
            </div>
            <InfoItem label="世界观" value={outline.worldSetting} />
            {outline.powerSystem && (
              <InfoItem label="力量体系" value={outline.powerSystem} />
            )}
          </div>
        </Section>

        {/* 人物设定 */}
        <Section
          title="人物设定"
          icon={<User className="w-5 h-5" />}
          expanded={expandedSections.has('characters')}
          onToggle={() => toggleSection('characters')}
        >
          <div className="space-y-4">
            <CharacterCard character={outline.protagonist} isProtagonist />
            {outline.supportingCharacters.map((char, i) => (
              <CharacterCard key={i} character={char} />
            ))}
          </div>
        </Section>

        {/* 三幕式结构 */}
        <Section
          title="三幕式结构"
          icon={<BookOpen className="w-5 h-5" />}
          expanded={expandedSections.has('structure')}
          onToggle={() => toggleSection('structure')}
        >
          <div className="space-y-4">
            <ActCard act={1} title="第一幕：建置" data={outline.threeActBreakdown.act1} />
            <ActCard act={2} title="第二幕：对抗" data={outline.threeActBreakdown.act2} />
            <ActCard act={3} title="第三幕：结局" data={outline.threeActBreakdown.act3} />
          </div>
        </Section>

        {/* 章节节拍 */}
        <Section
          title="章节节拍"
          icon={<BookOpen className="w-5 h-5" />}
          expanded={expandedSections.has('chapters')}
          onToggle={() => toggleSection('chapters')}
        >
          <div className="space-y-3">
            {outline.chapterBeats.map((beat, i) => (
              <ChapterBeatCard key={i} beat={beat} />
            ))}
          </div>
        </Section>

        {/* 爽点分布 */}
        <Section
          title="爽点分布"
          icon={<Zap className="w-5 h-5" />}
          expanded={expandedSections.has('coolpoints')}
          onToggle={() => toggleSection('coolpoints')}
        >
          <div className="space-y-3">
            {outline.coolPoints.map((point, i) => (
              <CoolPointCard key={i} point={point} />
            ))}
          </div>
        </Section>

        {/* 变体版本 */}
        {outlineVariations.length > 0 && (
          <div className="bg-white rounded-xl border border-gray-200 p-4">
            <h3 className="font-semibold mb-3">其他版本</h3>
            <div className="space-y-2">
              {outlineVariations.map((v) => (
                <button
                  key={v.id}
                  onClick={() => setOutline(v)}
                  className="w-full text-left p-3 rounded-lg border border-gray-200 hover:border-primary-300 transition-colors cursor-pointer"
                >
                  <div className="font-medium">{v.title}</div>
                  <div className="text-sm text-gray-500 line-clamp-1">{v.logline}</div>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* 底部操作 */}
        <div className="text-center pt-4">
          <Link
            to="/generate"
            className="inline-flex items-center gap-2 text-primary-600 hover:text-primary-700 cursor-pointer"
          >
            <RefreshCw className="w-4 h-4" />
            生成新的大纲
          </Link>
        </div>
      </motion.div>
    </div>
  );
}

// ========== 子组件 ==========

function Section({
  title,
  icon,
  expanded,
  onToggle,
  children,
}: {
  title: string;
  icon: React.ReactNode;
  expanded: boolean;
  onToggle: () => void;
  children: React.ReactNode;
}) {
  return (
    <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
      <button
        onClick={onToggle}
        className="w-full flex items-center justify-between p-4 hover:bg-gray-50 transition-colors cursor-pointer"
      >
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-primary-50 flex items-center justify-center text-primary-600">
            {icon}
          </div>
          <h2 className="font-semibold">{title}</h2>
        </div>
        {expanded ? (
          <ChevronUp className="w-5 h-5 text-gray-400" />
        ) : (
          <ChevronDown className="w-5 h-5 text-gray-400" />
        )}
      </button>
      {expanded && <div className="px-4 pb-4">{children}</div>}
    </div>
  );
}

function InfoItem({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div className="text-xs text-gray-400 mb-1">{label}</div>
      <div className="text-gray-900">{value}</div>
    </div>
  );
}

function CharacterCard({ character, isProtagonist = false }: { character: any; isProtagonist?: boolean }) {
  return (
    <div
      className={`p-4 rounded-xl ${
        isProtagonist ? 'bg-primary-50 border border-primary-200' : 'bg-gray-50'
      }`}
    >
      <div className="flex items-start justify-between mb-2">
        <div>
          <h4 className="font-semibold">{character.name}</h4>
          <div className="text-sm text-gray-500">{character.archetype}</div>
        </div>
        {isProtagonist && (
          <span className="px-2 py-1 bg-primary-500 text-white text-xs rounded-full">
            主角
          </span>
        )}
      </div>
      <div className="grid grid-cols-2 gap-2 text-sm">
        <div><span className="text-gray-400">动机：</span>{character.motivation}</div>
        <div><span className="text-gray-400">冲突：</span>{character.conflict}</div>
      </div>
      <div className="mt-2 text-sm">
        <span className="text-gray-400">弧光：</span>{character.arc}
      </div>
      <div className="mt-2 flex flex-wrap gap-1">
        {character.keyTraits?.map((trait: string, i: number) => (
          <span key={i} className="px-2 py-0.5 bg-white rounded text-xs text-gray-600">
            {trait}
          </span>
        ))}
      </div>
    </div>
  );
}

function ActCard({ act, title, data }: { act: number; title: string; data: any }) {
  const fields = {
    1: [
      { label: '开篇钩子', value: data.hook },
      { label: '激励事件', value: data.incitingIncident },
      { label: '第一转折点', value: data.firstPlotPoint },
    ],
    2: [
      { label: '中点高潮', value: data.midpoint },
      { label: '低谷时刻', value: data.lowPoint },
      { label: '第二转折点', value: data.secondPlotPoint },
    ],
    3: [
      { label: '高潮', value: data.climax },
      { label: '结局', value: data.resolution },
    ],
  };

  return (
    <div className="p-4 rounded-xl bg-gray-50">
      <div className="flex items-center justify-between mb-3">
        <h4 className="font-semibold">{title}</h4>
        <div className="text-sm text-gray-500">
          第 {data.chapters} 章 / {data.wordCount?.toLocaleString()} 字
        </div>
      </div>
      <div className="space-y-2 text-sm">
        {fields[act as keyof typeof fields].map((f) => (
          <div key={f.label}>
            <span className="text-gray-400">{f.label}：</span>
            {f.value}
          </div>
        ))}
      </div>
      {data.keyEvents && (
        <ul className="mt-3 list-disc list-inside text-sm text-gray-600">
          {data.keyEvents.map((event: string, i: number) => (
            <li key={i}>{event}</li>
          ))}
        </ul>
      )}
    </div>
  );
}

function ChapterBeatCard({ beat }: { beat: ChapterBeat }) {
  return (
    <div className="p-3 rounded-lg border border-gray-200">
      <div className="flex items-center justify-between mb-1">
        <h4 className="font-medium text-sm">{beat.title}</h4>
        <div className="text-xs text-gray-400">
          第 {beat.chapterRange} 章
        </div>
      </div>
      <p className="text-sm text-gray-600">{beat.summary}</p>
    </div>
  );
}

function CoolPointCard({ point }: { point: CoolPoint }) {
  const typeLabels: Record<string, string> = {
    'face-slap': '打脸',
    'power-up': '升级',
    reveal: '揭秘',
    revenge: '复仇',
    conquest: '征服',
    reversal: '反转',
  };

  return (
    <div className="p-3 rounded-lg border border-amber-200 bg-amber-50">
      <div className="flex items-center justify-between mb-1">
        <div className="flex items-center gap-2">
          <Zap className="w-4 h-4 text-amber-600" />
          <span className="font-medium text-amber-900">
            {typeLabels[point.type] || point.type}
          </span>
        </div>
        <span className="text-xs text-amber-600">约 {point.position}% 处</span>
      </div>
      <p className="text-sm text-amber-800">{point.description}</p>
    </div>
  );
}

// ========== 工具函数 ==========

function getGenreLabel(genre: string): string {
  const labels: Record<string, string> = {
    urban: '都市',
    xuanhuan: '玄幻',
    xianxia: '仙侠',
    history: '历史',
    scifi: '科幻',
    romance: '言情',
    suspense: '悬疑',
    wuxia: '武侠',
  };
  return labels[genre] || genre;
}

function formatOutlineAsMarkdown(outline: GeneratedOutline): string {
  return `# 《${outline.title}》

> ${outline.logline}

## 基本信息
- **类型**: ${getGenreLabel(outline.genre)}
- **目标字数**: ${outline.targetWordCount.toLocaleString()} 字

## 世界观
${outline.worldSetting}

## 主角
- **姓名**: ${outline.protagonist.name}
- **原型**: ${outline.protagonist.archetype}
- **动机**: ${outline.protagonist.motivation}
- **冲突**: ${outline.protagonist.conflict}
- **弧光**: ${outline.protagonist.arc}

## 三幕结构
### 第一幕：建置
- 开篇钩子: ${outline.threeActBreakdown.act1.hook}
- 激励事件: ${outline.threeActBreakdown.act1.incitingIncident}

### 第二幕：对抗
- 中点高潮: ${outline.threeActBreakdown.act2.midpoint}
- 低谷时刻: ${outline.threeActBreakdown.act2.lowPoint}

### 第三幕：结局
- 高潮: ${outline.threeActBreakdown.act3.climax}
- 结局: ${outline.threeActBreakdown.act3.resolution}
`;
}
